import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { VStack } from "@chakra-ui/react"
import { Input } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"

export interface VolumePressureInputsProps {
  required?: boolean
  helperText?: string
  defaultVolume?: number | null
  defaultMinPressure?: number | null
  error?: string | null
  onChange?: (volume: string, minPressure: string) => void
}

export interface VolumePressureInputsRef {
  getValues: () => { volume: string; minPressure: string }
  reset: () => void
}

/**
 * Reusable component for volume and minimum pressure input fields.
 * Used in monitoring activation workflows for dynamic_storage assets or when volume is required.
 * Uses local state and exposes values via ref to prevent unnecessary re-renders.
 */
const VolumePressureInputsComponent = forwardRef<VolumePressureInputsRef, VolumePressureInputsProps>(
  ({ required = false, helperText, defaultVolume, defaultMinPressure, error, onChange }, ref) => {
    // Initialise state with defaults if provided, otherwise empty strings
    const [volume, setVolume] = useState(defaultVolume?.toString() ?? "")
    const [minPressure, setMinPressure] = useState(defaultMinPressure?.toString() ?? "")

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getValues: () => ({ volume, minPressure }),
      reset: () => {
        // Reset to defaults if provided, otherwise empty strings
        setVolume(defaultVolume?.toString() ?? "")
        setMinPressure(defaultMinPressure?.toString() ?? "")
      },
    }), [volume, minPressure, defaultVolume, defaultMinPressure])

    useEffect(() => {
      onChange?.(volume, minPressure)
    }, [volume, minPressure])

    return (
      <VStack gap={3} align="stretch">
        <Field
          label="Volume (m³)"
          required={required}
          helperText={helperText || undefined}
          invalid={!!error}
          errorText={error || undefined}
        >
          <Input
            type="number"
            step="0.1"
            min="0.1"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            placeholder="Enter volume"
          />
        </Field>
        <Field label="Minimum Pressure (bar)" required={required} invalid={!!error} errorText={error || undefined}>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={minPressure}
            onChange={(e) => setMinPressure(e.target.value)}
            placeholder="Enter minimum pressure"
          />
        </Field>
      </VStack>
    )
  }
)

VolumePressureInputsComponent.displayName = "VolumePressureInputs"

export default VolumePressureInputsComponent
